import { WORKOS_API_URL, WORKOS_CLIENT_ID, WORKOS_DEV_MODE } from '@/lib/constants';
import { workosAuthBridge } from '@/lib/workos-auth-bridge';
import { AuthKitProvider, useAuth } from '@workos-inc/authkit-react';
import { createContext, useCallback, useContext, useEffect } from 'react';
import { useEnrichUser } from './useEnrichUser';
import { usePostHogIdentity } from './usePostHogIdentity';

type AuthRedirectParams = {
  state?: { returnTo?: unknown } | null;
};

export const onRedirectCallback = ({ state }: AuthRedirectParams) => {
  if (typeof state?.returnTo !== 'string') {
    return;
  }

  let url: URL;
  try {
    url = new URL(state.returnTo, window.location.origin);
  } catch {
    return;
  }

  // Replace the URL in place: a full page load here cancels the router's
  // in-flight chunk imports. The router's history observes replaceState.
  // AuthKit then synchronously replaces the history state with {}, dropping
  // the router's entry index, so restore it once that has run.
  if (url.origin === window.location.origin) {
    const routerState = window.history.state;
    window.history.replaceState(routerState, '', url.href);
    queueMicrotask(() => window.history.replaceState(routerState, ''));
  }
};

function useEnrichAuthHook() {
  const {
    getAccessToken,
    signIn: workOSSignIn,
    signOut: workOSSignOut,
    signUp: workOSSignUp,
    user: workOSUser,
    isLoading: workOSIsLoading,
  } = useAuth();
  const enrichUser = useEnrichUser();
  const { reset: resetPostHogIdentity } = usePostHogIdentity(enrichUser.user);

  // Register getAccessToken with the auth bridge for axios interceptors.
  // This allows the new Orval/axios-based API client to access the WorkOS token.
  useEffect(() => {
    workosAuthBridge.register(getAccessToken);
  }, [getAccessToken]);

  const signOut = useCallback(() => {
    workOSSignOut();
    resetPostHogIdentity();
    // Reset the auth bridge on sign out
    workosAuthBridge.reset();
  }, [workOSSignOut, resetPostHogIdentity]);

  const signIn = workOSSignIn;
  const signUp = workOSSignUp;
  return {
    isLoading: enrichUser.isLoading,
    // The WorkOS session-resolution state on its own (vs. the composite
    // isLoading above, which also waits on the /me backend call). Used to gate
    // router mount until we know whether the user is logged in.
    workOSIsLoading,
    user: enrichUser.user,
    apiFetch: enrichUser.apiFetch,
    workOSUser,
    getAccessToken,
    signIn,
    signOut,
    signUp,
  };
}

type UseEnrichAuthReturnType = ReturnType<typeof useEnrichAuthHook>;
const EnrichAuthContext = createContext<UseEnrichAuthReturnType>({} as UseEnrichAuthReturnType);

const EnrichAuthProvider__internal = ({ children }: { children: React.ReactNode }) => {
  const value = useEnrichAuthHook();
  return <EnrichAuthContext.Provider value={value}> {children} </EnrichAuthContext.Provider>;
};

export const EnrichAuthProvider = ({ children }: { children: React.ReactNode }) => {
  return (
    <AuthKitProvider
      clientId={WORKOS_CLIENT_ID}
      onRedirectCallback={onRedirectCallback}
      apiHostname={WORKOS_API_URL?.hostname}
      port={WORKOS_API_URL?.port ? Number(WORKOS_API_URL.port) : undefined}
      https={WORKOS_API_URL ? WORKOS_API_URL.protocol === 'https:' : undefined}
      devMode={WORKOS_DEV_MODE}
    >
      <EnrichAuthProvider__internal> {children} </EnrichAuthProvider__internal>
    </AuthKitProvider>
  );
};

export const useEnrichAuth = () => useContext(EnrichAuthContext);
